import {Directionality} from '@angular/cdk/bidi';
import {CdkStepper, CdkStepperModule} from '@angular/cdk/stepper';
import {NgTemplateOutlet} from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnInit,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import {ECheckoutStatus} from '@app/checkout/enums/checkout-status.enum';
import {CheckoutStep} from '@app/checkout/enums/checkout-step.enum';
import {selectCheckoutStatus} from '@app/checkout/state/checkout.selector';
import {PrimeNgModule} from '@app/shared/modules/prime-ng/prime-ng.module';
import {StepperService} from '@app/shared/services/stepper.service';
import {Store} from '@ngrx/store';
import {TranslateModule} from '@ngx-translate/core';
import {tap} from 'rxjs';

@Component({
  selector: 'app-stepper',
  templateUrl: './stepper.component.html',
  styleUrls: ['./stepper.component.scss'],
  providers: [{provide: CdkStepper, useExisting: StepperComponent}],
  imports: [NgTemplateOutlet, CdkStepperModule, PrimeNgModule, TranslateModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StepperComponent extends CdkStepper implements AfterViewInit, OnInit {
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly store = inject(Store);
  private readonly stepperService = inject(StepperService);

  readonly nextStepDisabled = input<boolean>(true);
  readonly nextLoading = input<boolean>(false);
  readonly previousLoading = input<boolean>(false);
  readonly cancelLoading = input<boolean>(false);
  readonly draftOrSubmitLoading = input<boolean>(false);

  readonly _nextStepDisabled = signal<boolean>(true);
  readonly _nextLoading = signal<boolean>(false);
  readonly _previousLoading = signal<boolean>(false);
  readonly _cancelLoading = signal<boolean>(false);
  readonly _draftOrSubmitLoading = signal<boolean>(false);

  readonly nextEvent = output<number>();
  readonly previousEvent = output<number>();
  readonly cancelEvent = output<number>();
  readonly draftOrSubmitEvent = output<number>();
  checkoutState: ECheckoutStatus | undefined;
  checkoutStatus = ECheckoutStatus;
  checkoutStep = CheckoutStep;

  constructor() {
    const directionality = inject(Directionality);
    const changeDetectionRef = inject(ChangeDetectorRef);
    const elementRef = inject<ElementRef<HTMLElement>>(ElementRef);

    super(directionality, changeDetectionRef, elementRef);
    effect(() => {
      this._nextStepDisabled.set(this.nextStepDisabled());
      this._nextLoading.set(this.nextLoading());
      this._previousLoading.set(this.previousLoading());
      this._cancelLoading.set(this.cancelLoading());
      this._draftOrSubmitLoading.set(this.draftOrSubmitLoading());
    });

    this.store
      .select(selectCheckoutStatus)
      .pipe(
        tap((checkoutStatus: ECheckoutStatus) => {
          this.checkoutState = checkoutStatus;
        })
      )
      .subscribe();
  }

  ngOnInit(): void {
    this.stepperService.navigationBehaviorSubject$.subscribe(currentStep => {
      this.selectStepByIndex(currentStep);
    });
  }

  override ngAfterViewInit(): void {
    super.ngAfterViewInit();

    const currentStep = this.steps.get(this.getCurrentStepIndex());

    currentStep?.select();
    this.changeDetectorRef.detectChanges();
  }

  selectStepByIndex(index: number): void {
    this.selectedIndex = index;
    this.scrollToTop();
  }

  private scrollToTop(): void {
    if (typeof globalThis === 'undefined' || !('requestAnimationFrame' in globalThis) || !('scrollTo' in globalThis)) {
      return;
    }

    globalThis.requestAnimationFrame(() => {
      globalThis.scrollTo({top: 0, left: 0});
    });
  }

  getCurrentStepIndex() {
    const firstNotCompletedStep = this.steps.filter(step => !step.completed)[0];

    if (!firstNotCompletedStep) {
      return this.steps.length - 1;
    }

    return this.steps
      .toArray()
      .map(step => step?.label)
      .indexOf(firstNotCompletedStep?.label);
  }

  nextStep() {
    this.nextEvent.emit(this.selectedIndex);
  }

  previousStep() {
    this.previousEvent.emit(this.selectedIndex);
  }

  cancelOrder() {
    this.cancelEvent.emit(this.selectedIndex);
  }

  draftOrSubmitOrder() {
    this.draftOrSubmitEvent.emit(this.selectedIndex);
  }
}
